import { useRef, useState } from 'react';

import {
  ArcElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  ScriptableContext,
  TimeScale,
  Title,
  Tooltip,
  TooltipModel,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { ChartDataPoint } from '@/api';
import ChartDemoBanner from '@/components/ChartDemoBanner/ChartDemoBanner';
import { TIMESPAN } from '@/enums/timespan';
import { useDemoContext } from '@/hooks/DemoContext';
import useResizeObserver from '@/hooks/useResizeObserver';
import useScreenSize from '@/hooks/useScreenSize';
import { ChartDataResponse } from '@/services/types/investment';

import { externalTooltipHandler } from './helpers/externalTooltipHandler';
import { getDisplayedData } from './helpers/getDisplayedData';
import styles from './styles.module.css';

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale
);

type Props = {
  timespan: TIMESPAN;
  donnees: ChartDataPoint[] | null;
};

const LineChart = ({ timespan, donnees }: Props) => {
  const { windowWidth } = useScreenSize();
  const tooltipTopPos = windowWidth > 745 ? 45 : 75;
  const { isDemo } = useDemoContext();

  const { t } = useTranslation();

  const linechartRef = useRef<HTMLDivElement>(null);
  const [lineChartWidth, setLineChartWidth] = useState(0);

  useResizeObserver(linechartRef, (entries) => {
    if (!entries[0]) return;
    const width = entries[0].contentRect.width ?? 0;
    if (width > 0) setLineChartWidth(width);
  });

  const donneesChart: ChartDataResponse[] = getDisplayedData((donnees as ChartDataResponse[]) ?? [], timespan);

  let minValue = 0;
  let maxValue = 0;

  if (donneesChart.length) {
    minValue = Math.min(...donneesChart.map((d) => d.m || d.valorisation || 0));
    maxValue = Math.max(...donneesChart.map((d) => d.m || d.valorisation || 0));
  } else {
    maxValue = 100;
  }

  const data = {
    labels: donneesChart.map((donneeD) => donneeD.d || donneeD.dateVL),

    datasets: [
      {
        data: donneesChart.map((donneeM) => donneeM.m || donneeM.valorisation),

        borderColor: '#77604B',
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const bgColor = ['#F1EBDF', '#FDFDFD'];
          if (!context.chart.chartArea) {
            return;
          }
          const {
            ctx,
            chartArea: { top, bottom },
          } = context.chart;

          const gardienBg = ctx.createLinearGradient(0, top, 0, bottom);
          gardienBg.addColorStop(0, bgColor[0]);
          gardienBg.addColorStop(1, bgColor[1]);
          return gardienBg;
        },

        pointBackgroundColor: 'transparent',
        pointStyle: false as const,

        fill: true,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    scales: {
      y: {
        display: false,
      },
      x: {
        display: false,
      },
    },
    maintainAspectRatio: false,

    interaction: {
      mode: 'index' as const,

      intersect: false,
    },

    plugins: {
      legend: {
        display: false,
      },

      tooltip: {
        enabled: false,

        external: (context: { chart: Chart; tooltip: TooltipModel<'line'> }) =>
          externalTooltipHandler(context, t, tooltipTopPos, lineChartWidth),
      },
    },
  };

  return (
    <div className={donneesChart.length ? '' : styles.linechartContainerEmpty}>
      {!donneesChart.length && (
        <div className={styles.linechartEmptyText}>Les données pour cette période ne sont pas disponibles.</div>
      )}
      <div
        className={
          donneesChart.length ? styles.contentLineChart : `${styles.contentLineChart} ${styles.contentLineChartEmpty}`
        }
      >
        <span className={`${styles.linechartYLabel} ${styles.linechartYLabelMax}`}>
          {t('format.currencyFlexible', { value: maxValue })}
        </span>

        <div className={styles.styleLineChart} ref={linechartRef}>
          <Line data={data} options={options} />
          {isDemo && <ChartDemoBanner />}
        </div>

        <span className={`${styles.linechartYLabel} ${styles.linechartYLabelMin}`}>
          {t('format.currencyFlexible', { value: minValue })}
        </span>
      </div>
    </div>
  );
};

export default LineChart;
